/**
 * Lecture d'un code-barres par la caméra, sans bibliothèque.
 *
 * <p>On s'appuie sur l'API `BarcodeDetector` du navigateur (Chrome et Edge sur
 * Android, Chrome sur macOS, Safari récent…). Là où elle manque — Firefox,
 * plusieurs Safari —, l'écran le dit et la saisie au clavier ou à la douchette
 * reste le chemin : une douchette tape le code puis « Entrée », comme un
 * clavier. Aucune dépendance lourde n'est chargée pour combler l'absence.
 */

/** Formats des articles du commerce et des étiquettes internes. */
export const FORMATS_CODE_BARRES = [
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'code_39',
  'itf',
  'qr_code',
];

/** Délai entre deux lectures de l'image, en millisecondes. */
export const INTERVALLE_LECTURE_MS = 250;

/**
 * Le scan par caméra est-il possible dans ce navigateur ?
 *
 * @param {Window} [win] fenêtre à examiner (injectable pour les tests)
 * @returns {boolean} vrai si `BarcodeDetector` et la caméra sont présents
 */
export const scanCameraDisponible = (win = globalThis.window) =>
  !!win && typeof win.BarcodeDetector === 'function' && !!win.navigator?.mediaDevices?.getUserMedia;

/**
 * Crée un détecteur limité aux formats que le navigateur sait lire.
 *
 * @param {Window} [win] fenêtre (injectable pour les tests)
 * @returns {Promise<object>} une instance de `BarcodeDetector`
 */
export const creerDetecteur = async (win = globalThis.window) => {
  const BD = win.BarcodeDetector;
  let formats = FORMATS_CODE_BARRES;
  try {
    if (typeof BD.getSupportedFormats === 'function') {
      const connus = await BD.getSupportedFormats();
      const communs = FORMATS_CODE_BARRES.filter((f) => connus.includes(f));
      if (communs.length) formats = communs;
    }
  } catch {
    // Liste indisponible : on demande les formats usuels, le navigateur ignore les autres.
  }
  return new BD({ formats });
};

/** Message affiché quand le navigateur ne propose pas la lecture par caméra. */
export const MESSAGE_SANS_CAMERA =
  'Ce navigateur ne sait pas lire un code-barres avec la caméra. Tape le code, ou branche une douchette : elle le saisit pour toi.';

/** Message affiché quand la caméra est refusée ou absente. */
export const MESSAGE_CAMERA_REFUSEE =
  'Caméra indisponible ou refusée. Autorise la caméra pour ce site, ou tape le code (douchette acceptée).';
